import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  ShoppingBag, Plus, Trash2, Save, Image, Video,
  Loader2, Pencil, X, Upload, Link as LinkIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const CATEGORIES = ["textile", "ustensile", "epicerie", "livre", "accessoire"];

const AdminShopTab = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stripeKey, setStripeKey] = useState("");
  const [savingStripe, setSavingStripe] = useState(false);

  const emptyProduct = {
    name: "", description: "", price: 0, category: "accessoire",
    featured_image: "", gallery_images: [], youtube_url: "", delivery_days: 5, active: true,
  };

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/shop/products`);
      setProducts(res.data);
    } catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!editing.name || !editing.price) { toast.error("Nom et prix requis"); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        description: editing.description,
        price: parseFloat(editing.price),
        category: editing.category,
        featured_image: editing.featured_image,
        gallery_images: editing.gallery_images.filter(Boolean),
        youtube_url: editing.youtube_url || null,
        delivery_days: parseInt(editing.delivery_days) || 5,
        active: editing.active,
      };
      if (editing.id) {
        await axios.put(`${API}/admin/shop/products/${editing.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Produit mis à jour !");
      } else {
        await axios.post(`${API}/admin/shop/products`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Produit créé !");
      }
      setEditing(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/admin/shop/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Produit supprimé");
      fetchProducts();
    } catch { toast.error("Erreur"); }
  };

  const handleSaveStripe = async () => {
    if (!stripeKey.startsWith("sk_")) { toast.error("Clé Stripe invalide (doit commencer par sk_)"); return; }
    setSavingStripe(true);
    try {
      const form = new FormData();
      form.append("key", stripeKey);
      await axios.post(`${API}/admin/settings/stripe`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Clé Stripe mise à jour !");
      setStripeKey("");
    } catch (err) { toast.error("Erreur"); } finally { setSavingStripe(false); }
  };

  const addGalleryImage = () => {
    setEditing((e) => ({ ...e, gallery_images: [...e.gallery_images, ""] }));
  };

  const updateGalleryImage = (index, value) => {
    setEditing((e) => {
      const imgs = [...e.gallery_images];
      imgs[index] = value;
      return { ...e, gallery_images: imgs };
    });
  };

  const removeGalleryImage = (index) => {
    setEditing((e) => ({
      ...e,
      gallery_images: e.gallery_images.filter((_, i) => i !== index),
    }));
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  // Product Editor
  if (editing) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" />
            {editing.id ? "Modifier le produit" : "Nouveau produit"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Nom *</label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="product-name-input" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Prix (€) *</label>
              <Input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Délai livraison (jours)</label>
              <Input type="number" value={editing.delivery_days} onChange={(e) => setEditing({ ...editing, delivery_days: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Catégorie</label>
              <select
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="w-full border rounded-md h-10 px-3 text-sm"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm">Actif</span>
              </label>
            </div>
          </div>

          {/* Featured Image */}
          <div>
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Image className="w-3 h-3" /> Image principale (URL)
            </label>
            <Input value={editing.featured_image} onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} placeholder="https://..." />
            {editing.featured_image && (
              <img src={editing.featured_image} alt="Preview" className="mt-2 h-24 w-24 object-cover rounded-lg" />
            )}
          </div>

          {/* Gallery */}
          <div>
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-2">
              <Image className="w-3 h-3" /> Galerie photos
            </label>
            {editing.gallery_images.map((url, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input
                  value={url}
                  onChange={(e) => updateGalleryImage(i, e.target.value)}
                  placeholder="URL de l'image..."
                  className="flex-1"
                />
                {url && <img src={url} alt="" className="w-10 h-10 object-cover rounded" />}
                <Button variant="ghost" size="sm" onClick={() => removeGalleryImage(i)}>
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addGalleryImage} className="gap-1">
              <Plus className="w-3 h-3" /> Ajouter une photo
            </Button>
          </div>

          {/* YouTube */}
          <div>
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Video className="w-3 h-3" /> Vidéo YouTube (URL)
            </label>
            <Input
              value={editing.youtube_url || ""}
              onChange={(e) => setEditing({ ...editing, youtube_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1 rounded-full gap-2" data-testid="save-product-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </Button>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Annuler</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Product List + Stripe Settings
  return (
    <div className="space-y-4">
      {/* Stripe Key */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
            <LinkIcon className="w-4 h-4" /> Clé Stripe
          </h3>
          <div className="flex gap-2">
            <Input
              type="password"
              value={stripeKey}
              onChange={(e) => setStripeKey(e.target.value)}
              placeholder="sk_live_... ou sk_test_..."
              className="flex-1"
              data-testid="stripe-key-input"
            />
            <Button onClick={handleSaveStripe} disabled={savingStripe || !stripeKey} size="sm" className="rounded-full gap-1">
              {savingStripe ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Sauver
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Products */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Produits ({products.length})</h3>
        <Button onClick={() => setEditing({ ...emptyProduct })} size="sm" className="rounded-full gap-1" data-testid="add-product-btn">
          <Plus className="w-3 h-3" /> Ajouter
        </Button>
      </div>

      {products.map((p) => (
        <Card key={p.id}>
          <CardContent className="p-3 flex items-center gap-3">
            {p.featured_image ? (
              <img src={p.featured_image} alt={p.name} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-5 h-5 text-gray-300" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{p.name}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-bold text-primary">{p.price?.toFixed(2)}€</span>
                <Badge variant="outline" className="text-[10px]">{p.category}</Badge>
                {p.gallery_images?.length > 0 && <Badge variant="outline" className="text-[10px]"><Image className="w-2.5 h-2.5 mr-0.5" />{p.gallery_images.length}</Badge>}
                {p.youtube_url && <Badge variant="outline" className="text-[10px]"><Video className="w-2.5 h-2.5" /></Badge>}
              </div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setEditing({ ...p })} data-testid={`edit-product-${p.id}`}>
                <Pencil className="w-4 h-4 text-blue-500" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminShopTab;
